import React, { useState, useEffect } from 'react';
import { 
  FaStore, 
  FaBox, 
  FaShoppingCart, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaTag,
  FaImage,
  FaDollarSign,
  FaUtensils,
  FaCar,
  FaMobileAlt,
  FaTshirt,
  FaLaptop,
  FaUpload
} from 'react-icons/fa';
import { FiSearch, FiFilter, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import './StoreManagement.css';

interface Product {
  id: number;
  name: string;
  description: string;
  price_usd: number;
  price_lrd: number;
  stock: number;
  category: string;
  sub_category: string;
  product_type: 'physical' | 'digital' | 'service' | 'food' | 'ride';
  image_url: string;
  images: string[];
  status: 'active' | 'inactive';
  featured: boolean;
  sales_count: number;
  rating: number;
  reviews_count: number;
  created_at: string;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  items: any[];
  subtotal_usd: number;
  subtotal_lrd: number;
  total_usd: number;
  total_lrd: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'cancelled' | 'refunded';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  delivery_status: 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled';
  delivery_address: string;
  notes: string;
  created_at: string;
}

interface StoreStats {
  products: {
    total: number;
    active: number;
    total_stock: number;
    total_sales: number;
  };
  orders: {
    total: number;
    pending: number;
    processing: number;
    completed: number;
    cancelled: number;
    total_revenue_usd: number;
    total_revenue_lrd: number;
  };
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const StoreManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'products' | 'orders'>('products');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState<Product | null>(null);
  const [showOrderDetail, setShowOrderDetail] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [stats, setStats] = useState<StoreStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });

  const token = localStorage.getItem('merchant_token');

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price_usd: 0,
    price_lrd: 0,
    stock: 0,
    category: '',
    sub_category: '',
    product_type: 'physical' as const,
    image_url: '',
    status: 'active' as const,
    featured: false
  });

  // Image upload handler
  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('image', file);
    
    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 200);
    
    try {
      const response = await fetch(`${API_URL}/merchants/store/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (data.success) {
        setNewProduct({ ...newProduct, image_url: data.data.image_url });
        showNotification('Image uploaded successfully', 'success');
      } else {
        showNotification(data.error || 'Upload failed', 'error');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Upload error:', error);
      showNotification('Failed to upload image', 'error');
    } finally {
      setTimeout(() => {
        setUploadingImage(false);
        setUploadProgress(0);
      }, 500);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification('Please select an image file', 'error');
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showNotification('Image must be less than 5MB', 'error');
        return;
      }
      
      handleImageUpload(file);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedType !== 'all') params.append('type', selectedType);
      
      const response = await fetch(`${API_URL}/merchants/store/products?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setProducts(data.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      showNotification('Failed to load products', 'error');
    }
  };

  // Fetch orders
  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/store/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setOrders(data.data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showNotification('Failed to load orders', 'error');
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/store/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch store stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/store/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Add product
  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/merchants/store/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newProduct)
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Product added successfully', 'success');
        setShowAddProduct(false);
        setNewProduct({
          name: '',
          description: '',
          price_usd: 0,
          price_lrd: 0,
          stock: 0,
          category: '',
          sub_category: '',
          product_type: 'physical',
          image_url: '',
          status: 'active',
          featured: false
        });
        fetchProducts();
        fetchStats();
        fetchCategories();
      } else {
        showNotification(data.error || 'Failed to add product', 'error');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      showNotification('Failed to add product', 'error');
    }
  };

  // Update product
  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showEditProduct) return;
    
    try {
      const response = await fetch(`${API_URL}/merchants/store/products/${showEditProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(showEditProduct)
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Product updated successfully', 'success');
        setShowEditProduct(null);
        fetchProducts();
        fetchStats();
      } else {
        showNotification(data.error || 'Failed to update product', 'error');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      showNotification('Failed to update product', 'error');
    }
  };

  // Delete product
  const handleDeleteProduct = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/merchants/store/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Product deleted successfully', 'success');
        fetchProducts();
        fetchStats();
      } else {
        showNotification(data.error || 'Failed to delete product', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showNotification('Failed to delete product', 'error');
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderNumber: string, status: string) => {
    try {
      const response = await fetch(`${API_URL}/merchants/store/orders/${orderNumber}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        showNotification(`Order ${status}`, 'success');
        fetchOrders();
        setShowOrderDetail(null);
      } else {
        showNotification(data.error || 'Failed to update order', 'error');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      showNotification('Failed to update order', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchProducts(),
        fetchOrders(),
        fetchCategories(),
        fetchStats()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else {
      fetchOrders();
    }
  }, [searchTerm, selectedCategory, selectedType, activeTab]);

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD' = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'processing': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      case 'refunded': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed': return <FiCheckCircle />;
      case 'pending': return <FiClock />;
      case 'cancelled': return <FiXCircle />;
      default: return null;
    }
  };

  const getProductTypeIcon = (type: string) => {
    switch(type) {
      case 'food': return <FaUtensils />;
      case 'ride': return <FaCar />;
      case 'digital': return <FaMobileAlt />;
      case 'service': return <FaTag />;
      default: return <FaBox />;
    }
  };

  const productTypes = [
    { value: 'physical', label: 'Physical Product', icon: <FaBox /> },
    { value: 'digital', label: 'Digital Product', icon: <FaMobileAlt /> },
    { value: 'service', label: 'Service', icon: <FaTag /> },
    { value: 'food', label: 'Food Item', icon: <FaUtensils /> },
    { value: 'ride', label: 'Ride Service', icon: <FaCar /> }
  ];

  if (loading) {
    return (
      <div className="store-management">
        <div className="loading-store">
          <div className="spinner"></div>
          <p>Loading store...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="store-management">
      {notification.show && (
        <div className={`store-notification ${notification.type}`}>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="store-header">
        <h2>
          <FaStore /> Store Management
        </h2>
        {activeTab === 'products' && (
          <button className="add-product-btn" onClick={() => setShowAddProduct(true)}>
            <FaPlus /> Add Product
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="store-stats">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <span>Total Products</span>
              <h3>{stats.products.total}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <span>Active Products</span>
              <h3>{stats.products.active}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <span>Revenue (USD)</span>
              <h3>{formatCurrency(stats.orders.total_revenue_usd, 'USD')}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-info">
              <span>Total Orders</span>
              <h3>{stats.orders.total}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <span>Pending Orders</span>
              <h3 className="pending">{stats.orders.pending}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <span>Completed Orders</span>
              <h3 className="completed">{stats.orders.completed}</h3>
            </div>
          </div>
        </div>
      )}

      <div className="store-tabs">
        <button className={activeTab === 'products' ? 'active' : ''} onClick={() => setActiveTab('products')}>
          <FaBox /> Products ({products.length})
        </button>
        <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>
          <FaShoppingCart /> Orders ({orders.length})
        </button>
      </div>

      {activeTab === 'products' && (
        <>
          <div className="products-filters">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <FiFilter />
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <FiFilter />
              <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                <option value="all">All Types</option>
                {productTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <button className="refresh-btn" onClick={() => fetchProducts()}>
              <FiRefreshCw />
            </button>
          </div>

          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} />
                  ) : (
                    <div className="image-placeholder">
                      {getProductTypeIcon(product.product_type)}
                    </div>
                  )}
                  <div className={`product-status ${product.status}`}>
                    {product.status}
                  </div>
                  {product.product_type && (
                    <div className="product-type-badge">
                      {getProductTypeIcon(product.product_type)} {product.product_type}
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p className="product-description">{product.description?.substring(0, 80)}...</p>
                  <p className="product-category">{product.category}</p>
                  <div className="product-prices">
                    <span className="usd-price">{formatCurrency(product.price_usd, 'USD')}</span>
                    <span className="lrd-price">{formatCurrency(product.price_lrd, 'LRD')}</span>
                  </div>
                  <div className="product-stock">
                    <span>Stock: {product.stock} units</span>
                    <span>Sales: {product.sales_count}</span>
                    {product.featured && <span className="featured-badge">⭐ Featured</span>}
                  </div>
                  <div className="product-actions">
                    <button className="edit-btn" onClick={() => setShowEditProduct(product)}>
                      <FaEdit /> Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteProduct(product.id)}>
                      <FaTrash /> Delete
                    </button>
                    <button className="view-btn">
                      <FaEye /> View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div className="empty-products">
              <FaBox size={48} />
              <p>No products found</p>
              <button className="create-btn" onClick={() => setShowAddProduct(true)}>
                Add Your First Product
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'orders' && (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total (USD)</th>
                <th>Total (LRD)</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Delivery</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td className="order-id">{order.order_number}</td>
                  <td>
                    <div className="customer-info">
                      <strong>{order.customer_name}</strong>
                      <small>{order.customer_email}</small>
                    </div>
                  </td>
                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>{order.items?.length || 0}</td>
                  <td className="usd-amount">{order.total_usd > 0 ? formatCurrency(order.total_usd, 'USD') : '-'}</td>
                  <td className="lrd-amount">{order.total_lrd > 0 ? formatCurrency(order.total_lrd, 'LRD') : '-'}</td>
                  <td>
                    <span className="order-status" style={{ background: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}>
                      {getStatusIcon(order.status)} {order.status}
                    </span>
                  </td>
                  <td>
                    <span className={`payment-status ${order.payment_status}`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td>
                    <span className={`delivery-status ${order.delivery_status}`}>
                      {order.delivery_status}
                    </span>
                  </td>
                  <td>
                    <button className="view-order-btn" onClick={() => setShowOrderDetail(order)}>
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {orders.length === 0 && (
            <div className="empty-orders">
              <FaShoppingCart size={48} />
              <p>No orders yet</p>
            </div>
          )}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Product</h3>
            <form onSubmit={handleAddProduct} className="product-form">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Product Type</label>
                  <select
                    value={newProduct.product_type}
                    onChange={(e) => setNewProduct({ ...newProduct, product_type: e.target.value as any })}
                  >
                    {productTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                    placeholder="e.g., Electronics, Clothing, Food"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProduct.price_usd}
                    onChange={(e) => setNewProduct({ ...newProduct, price_usd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Price (LRD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProduct.price_lrd}
                    onChange={(e) => setNewProduct({ ...newProduct, price_lrd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({ ...newProduct, stock: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Product Image</label>
                  <div className="image-upload-area">
                    {newProduct.image_url ? (
                      <div className="image-preview">
                        <img src={newProduct.image_url} alt="Product preview" />
                        <button 
                          type="button" 
                          className="remove-image-btn"
                          onClick={() => setNewProduct({ ...newProduct, image_url: '' })}
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            disabled={uploadingImage}
                            hidden
                          />
                          <div className="upload-icon">
                            {uploadingImage ? (
                              <>
                                <div className="upload-spinner"></div>
                                <span>Uploading... {uploadProgress}%</span>
                              </>
                            ) : (
                              <>
                                <FaUpload size={32} />
                                <span>Click to upload image</span>
                                <small>JPG, PNG, GIF up to 5MB</small>
                              </>
                            )}
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newProduct.status}
                    onChange={(e) => setNewProduct({ ...newProduct, status: e.target.value as any })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newProduct.featured}
                      onChange={(e) => setNewProduct({ ...newProduct, featured: e.target.checked })}
                    />
                    Featured Product
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddProduct(false)}>Cancel</button>
                <button type="submit" className="submit-btn">Add Product</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProduct && (
        <div className="modal-overlay" onClick={() => setShowEditProduct(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Product</h3>
            <form onSubmit={handleUpdateProduct} className="product-form">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={showEditProduct.name}
                  onChange={(e) => setShowEditProduct({ ...showEditProduct, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={showEditProduct.description || ''}
                  onChange={(e) => setShowEditProduct({ ...showEditProduct, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={showEditProduct.price_usd}
                    onChange={(e) => setShowEditProduct({ ...showEditProduct, price_usd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Price (LRD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={showEditProduct.price_lrd}
                    onChange={(e) => setShowEditProduct({ ...showEditProduct, price_lrd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    value={showEditProduct.stock}
                    onChange={(e) => setShowEditProduct({ ...showEditProduct, stock: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={showEditProduct.category || ''}
                    onChange={(e) => setShowEditProduct({ ...showEditProduct, category: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={showEditProduct.status}
                    onChange={(e) => setShowEditProduct({ ...showEditProduct, status: e.target.value as any })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={showEditProduct.featured}
                      onChange={(e) => setShowEditProduct({ ...showEditProduct, featured: e.target.checked })}
                    />
                    Featured Product
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEditProduct(null)}>Cancel</button>
                <button type="submit" className="submit-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetail && (
        <div className="modal-overlay" onClick={() => setShowOrderDetail(null)}>
          <div className="modal-content order-detail" onClick={(e) => e.stopPropagation()}>
            <h3>Order Details</h3>
            <div className="order-info">
              <div className="info-row">
                <span>Order Number:</span>
                <strong>{showOrderDetail.order_number}</strong>
              </div>
              <div className="info-row">
                <span>Customer:</span>
                <span>{showOrderDetail.customer_name}</span>
              </div>
              <div className="info-row">
                <span>Email:</span>
                <span>{showOrderDetail.customer_email}</span>
              </div>
              <div className="info-row">
                <span>Phone:</span>
                <span>{showOrderDetail.customer_phone}</span>
              </div>
              <div className="info-row">
                <span>Delivery Address:</span>
                <span>{showOrderDetail.delivery_address || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span>Order Date:</span>
                <span>{new Date(showOrderDetail.created_at).toLocaleString()}</span>
              </div>
            </div>

            <div className="order-items">
              <h4>Items</h4>
              <table className="items-table">
                <thead>
                  <tr><th>Product</th><th>Quantity</th><th>Price</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {showOrderDetail.items?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.price, showOrderDetail.currency as 'USD' | 'LRD')}</td>
                      <td>{formatCurrency(item.price * item.quantity, showOrderDetail.currency as 'USD' | 'LRD')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="order-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatCurrency(showOrderDetail.subtotal_usd, 'USD')} / {formatCurrency(showOrderDetail.subtotal_lrd, 'LRD')}</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatCurrency(showOrderDetail.total_usd, 'USD')} / {formatCurrency(showOrderDetail.total_lrd, 'LRD')}</span>
              </div>
            </div>

            <div className="order-status-update">
              <label>Update Status:</label>
              <div className="status-buttons">
                <button onClick={() => handleUpdateOrderStatus(showOrderDetail.order_number, 'pending')} className="status-btn pending">Pending</button>
                <button onClick={() => handleUpdateOrderStatus(showOrderDetail.order_number, 'processing')} className="status-btn processing">Processing</button>
                <button onClick={() => handleUpdateOrderStatus(showOrderDetail.order_number, 'completed')} className="status-btn completed">Completed</button>
                <button onClick={() => handleUpdateOrderStatus(showOrderDetail.order_number, 'cancelled')} className="status-btn cancelled">Cancelled</button>
              </div>
            </div>

            <div className="modal-actions">
              <button className="close-btn" onClick={() => setShowOrderDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreManagement;
